import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Search, Phone, Mail, UserPlus, Wrench, ArrowLeft, Pencil, Save, X, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// CFDI 4.0 — SAT Fiscal Regime catalogue (most common)
const SAT_REGIMES = [
  ['601', 'General de Ley Personas Morales'],
  ['603', 'Personas Morales con Fines no Lucrativos'],
  ['605', 'Sueldos y Salarios e Ingresos Asimilados a Salarios'],
  ['606', 'Arrendamiento'],
  ['607', 'Régimen de Enajenación o Adquisición de Bienes'],
  ['608', 'Demás ingresos'],
  ['610', 'Residentes en el Extranjero sin Establecimiento Permanente en México'],
  ['611', 'Ingresos por Dividendos (socios y accionistas)'],
  ['612', 'Personas Físicas con Actividades Empresariales y Profesionales'],
  ['614', 'Ingresos por intereses'],
  ['615', 'Régimen de los ingresos por obtención de premios'],
  ['616', 'Sin obligaciones fiscales'],
  ['620', 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos'],
  ['621', 'Incorporación Fiscal'],
  ['622', 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras'],
  ['623', 'Opcional para Grupos de Sociedades'],
  ['624', 'Coordinados'],
  ['625', 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas'],
  ['626', 'Régimen Simplificado de Confianza'],
];

export default function Clients() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [searched, setSearched] = useState(false);
  const [showForm, setShowForm] = useState(false);
  // Inline detail view (replaces the previous modal)
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const FISCAL_DEFAULTS = { fiscal_name: '', fiscal_rfc: '', fiscal_regime: '', fiscal_zip: '', fiscal_email: '' };
  const [editForm, setEditForm] = useState({ name: '', phone: '', phone2: '', email: '', notes: '', ...FISCAL_DEFAULTS });
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({ name: '', phone: '', phone2: '', email: '', notes: '', ...FISCAL_DEFAULTS });

  useEffect(() => { fetchClients(); /* eslint-disable-next-line */ }, []);

  const fetchClients = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const res = await axios.get(`${API}/api/clients${params}`, { headers: getAuthHeaders() });
      setClients(res.data);
      if (search.trim()) setSearched(true);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/clients`, form, { headers: getAuthHeaders() });
      setShowForm(false);
      setForm({ name: '', phone: '', phone2: '', email: '', notes: '', ...FISCAL_DEFAULTS });
      fetchClients();
      return res.data;
    } catch (err) { console.error(err); return null; }
  };

  const handleCreateAndRepair = async () => {
    if (!form.name || !form.phone) return;
    try {
      const res = await axios.post(`${API}/api/clients`, form, { headers: getAuthHeaders() });
      setShowForm(false);
      setForm({ name: '', phone: '', phone2: '', email: '', notes: '', ...FISCAL_DEFAULTS });
      navigate('/repairs', { state: { newRepairClient: res.data } });
    } catch (err) { console.error(err); }
  };

  const openRegisterWithSearch = () => {
    const isPhone = /^\d+$/.test(search.trim());
    setForm({
      name: isPhone ? '' : search.trim(),
      phone: isPhone ? search.trim() : '',
      phone2: '', email: '', notes: '', ...FISCAL_DEFAULTS,
    });
    setShowForm(true);
  };

  const openDetail = async (client) => {
    setDetailLoading(true);
    setSelectedClient({ ...client });
    setEditMode(false);
    setEditError('');
    try {
      const res = await axios.get(`${API}/api/clients/${client.id}`, { headers: getAuthHeaders() });
      setSelectedClient(res.data);
    } catch (err) {
      // Keep the row data so the view still renders something
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedClient(null);
    setEditMode(false);
    setEditError('');
  };

  const startEdit = () => {
    if (!selectedClient) return;
    setEditForm({
      name: selectedClient.name || '',
      phone: selectedClient.phone || '',
      phone2: selectedClient.phone2 || '',
      email: selectedClient.email || '',
      notes: selectedClient.notes || '',
      fiscal_name: selectedClient.fiscal_name || '',
      fiscal_rfc: selectedClient.fiscal_rfc || '',
      fiscal_regime: selectedClient.fiscal_regime || '',
      fiscal_zip: selectedClient.fiscal_zip || '',
      fiscal_email: selectedClient.fiscal_email || '',
    });
    setEditError('');
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditError('');
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!selectedClient) return;
    if (!editForm.name.trim() || !editForm.phone.trim()) {
      setEditError('Nombre y teléfono son obligatorios');
      return;
    }
    setSaving(true); setEditError('');
    try {
      const res = await axios.put(`${API}/api/clients/${selectedClient.id}`, editForm, { headers: getAuthHeaders() });
      // Merge updated fields onto current selectedClient (preserve repairs/sales)
      setSelectedClient(prev => ({ ...prev, ...res.data }));
      setEditMode(false);
      // Refresh the underlying list so the row reflects the new values
      fetchClients();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- DETAIL VIEW ----------------
  if (selectedClient) {
    const c = selectedClient;
    return (
      <div data-testid="client-detail-page">
        <button onClick={closeDetail} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4 transition-colors" data-testid="client-detail-back">
          <ArrowLeft className="w-4 h-4" /> Volver al listado
        </button>

        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']" data-testid="client-detail-name">{c.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Cliente registrado el {new Date(c.created_at).toLocaleDateString('es-MX')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/repairs', { state: { newRepairClient: c } })}
              className="bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900 text-sm"
              data-testid="client-new-repair-btn"
            >
              <Wrench className="w-4 h-4 mr-1" /> Nueva Reparacion
            </Button>
            {isAdmin && !editMode && (
              <Button onClick={startEdit} variant="outline" className="text-sm" data-testid="client-edit-btn">
                <Pencil className="w-4 h-4 mr-1" /> Editar
              </Button>
            )}
          </div>
        </div>

        {/* Info card (view mode) OR edit form */}
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
          {editMode ? (
            <form onSubmit={saveEdit} className="space-y-3" data-testid="client-edit-form">
              <h3 className="text-sm font-semibold text-[#1D3C6E] mb-2 font-['Outfit']">Editar información</h3>
              <div>
                <label className="text-xs font-medium text-slate-600">Nombre *</label>
                <Input data-testid="client-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Teléfono *</label>
                  <Input data-testid="client-edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Teléfono 2</label>
                  <Input data-testid="client-edit-phone2" value={editForm.phone2} onChange={(e) => setEditForm({ ...editForm, phone2: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <Input data-testid="client-edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Notas</label>
                <textarea data-testid="client-edit-notes" className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[60px]" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>

              {/* Datos de Facturación (CFDI 4.0) */}
              <div className="mt-2 pt-3 border-t border-slate-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#1D3C6E] mb-2">Datos de Facturación</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Nombre / Razón Social</label>
                    <Input data-testid="client-edit-fiscal-name" value={editForm.fiscal_name} onChange={(e) => setEditForm({ ...editForm, fiscal_name: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">RFC</label>
                      <Input data-testid="client-edit-fiscal-rfc" value={editForm.fiscal_rfc} onChange={(e) => setEditForm({ ...editForm, fiscal_rfc: e.target.value.toUpperCase() })} maxLength={13} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Código Postal</label>
                      <Input data-testid="client-edit-fiscal-zip" value={editForm.fiscal_zip} onChange={(e) => setEditForm({ ...editForm, fiscal_zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} maxLength={5} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Régimen Fiscal</label>
                    <select data-testid="client-edit-fiscal-regime" className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" value={editForm.fiscal_regime} onChange={(e) => setEditForm({ ...editForm, fiscal_regime: e.target.value })}>
                      <option value="">Selecciona un régimen…</option>
                      {SAT_REGIMES.map(([code, label]) => (
                        <option key={code} value={code}>{code} — {label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Correo electrónico (facturación)</label>
                    <Input data-testid="client-edit-fiscal-email" type="email" value={editForm.fiscal_email} onChange={(e) => setEditForm({ ...editForm, fiscal_email: e.target.value })} />
                  </div>
                </div>
              </div>

              {editError && (
                <div className="flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-700" data-testid="client-edit-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{editError}</span>
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={cancelEdit} data-testid="client-edit-cancel">
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#1D3C6E] hover:bg-[#152B50] disabled:opacity-60" data-testid="client-edit-save">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </form>
          ) : (
            <div data-testid="client-info-view">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <InfoRow icon={<Phone className="w-4 h-4 text-[#1D3C6E]" />} label="Teléfono" value={c.phone || '—'} />
                <InfoRow icon={<Phone className="w-4 h-4 text-[#1D3C6E]" />} label="Teléfono 2" value={c.phone2 || '—'} />
                <InfoRow icon={<Mail className="w-4 h-4 text-[#1D3C6E]" />} label="Email" value={c.email || '—'} />
                <InfoRow label="Sucursal" value={c.branch || '—'} />
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Notas</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.notes || '—'}</p>
                </div>
              </div>
              {/* Datos de Facturación */}
              <div className="mt-4 pt-3 border-t border-slate-200" data-testid="client-fiscal-view">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#1D3C6E] mb-2">Datos de Facturación</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <InfoRow label="Nombre / Razón Social" value={c.fiscal_name || '—'} />
                  <InfoRow label="RFC" value={c.fiscal_rfc || '—'} />
                  <InfoRow
                    label="Régimen Fiscal"
                    value={(() => {
                      if (!c.fiscal_regime) return '—';
                      const match = SAT_REGIMES.find(([code]) => code === c.fiscal_regime);
                      return match ? `${c.fiscal_regime} — ${match[1]}` : c.fiscal_regime;
                    })()}
                  />
                  <InfoRow label="Código Postal" value={c.fiscal_zip || '—'} />
                  <InfoRow icon={<Mail className="w-4 h-4 text-[#1D3C6E]" />} label="Correo (facturación)" value={c.fiscal_email || '—'} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* History */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-[#1D3C6E] mb-3 font-['Outfit']">Historial de Reparaciones ({c.repairs?.length || 0})</h3>
            {detailLoading ? <p className="text-xs text-slate-400">Cargando...</p>
              : c.repairs?.length > 0 ? (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {c.repairs.map(r => (
                  <button key={r.id} onClick={() => navigate(`/repairs/${r.id}`)} className="w-full text-left text-xs bg-slate-50 hover:bg-slate-100 p-2 rounded flex justify-between transition-colors">
                    <span className="font-medium">{r.ticket} <span className="text-slate-400">— {r.device_model || 'Dispositivo'}</span></span>
                    <span className="text-slate-500">{r.status}</span>
                  </button>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400">Sin reparaciones</p>}
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-[#1D3C6E] mb-3 font-['Outfit']">Historial de Compras ({c.sales?.length || 0})</h3>
            {detailLoading ? <p className="text-xs text-slate-400">Cargando...</p>
              : c.sales?.length > 0 ? (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {c.sales.map(s => (
                  <div key={s.id} className="text-xs bg-slate-50 p-2 rounded flex justify-between">
                    <span className="font-medium">${(s.total || 0).toFixed(2)}</span>
                    <span className="text-slate-500">{new Date(s.created_at).toLocaleDateString('es-MX')}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-slate-400">Sin compras</p>}
          </div>
        </div>

        {/* Returns / Exchanges / Warranties */}
        {(c.returns && c.returns.length > 0) && (
          <div className="bg-white rounded-lg border border-slate-200 p-5 mt-4" data-testid="client-returns-history">
            <h3 className="text-sm font-semibold text-[#1D3C6E] mb-3 font-['Outfit']">Devoluciones, cambios y garantías ({c.returns.length})</h3>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {c.returns.map(r => {
                const labels = { return: 'Devolución', exchange: 'Cambio', warranty: 'Garantía' };
                const colors = { return: 'bg-rose-100 text-rose-700', exchange: 'bg-amber-100 text-amber-700', warranty: 'bg-emerald-100 text-emerald-700' };
                return (
                  <button key={r.id} onClick={() => navigate(`/returns/${r.id}`)} className="w-full text-left text-xs bg-slate-50 hover:bg-slate-100 p-2 rounded flex items-center justify-between transition-colors">
                    <div className="flex items-center gap-2">
                      <span className={`status-badge ${colors[r.type] || 'bg-slate-100 text-slate-700'}`}>{labels[r.type] || r.type}</span>
                      <span className="text-slate-500">→ Venta {r.original_sale_ticket || (r.original_sale_id ? r.original_sale_id.slice(-8) : '')}</span>
                      <span className="text-slate-400">{new Date(r.created_at).toLocaleDateString('es-MX')}</span>
                    </div>
                    <span className={`font-semibold ${(r.resolution_amount || 0) < 0 ? 'text-rose-600' : 'text-[#1D3C6E]'}`}>
                      {(r.resolution_amount || 0) < 0 ? '-' : ''}${Math.abs(r.resolution_amount || 0).toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------- LIST VIEW ----------------
  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Clientes</h1>
        <Button onClick={() => { setForm({ name: '', phone: '', phone2: '', email: '', notes: '' }); setShowForm(true); }} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="new-client-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="client-search" className="pl-10" placeholder="Buscar por nombre, telefono o email..."
          value={search} onChange={(e) => { setSearch(e.target.value); setSearched(false); }} onKeyDown={(e) => e.key === 'Enter' && fetchClients()} />
      </div>

      {searched && clients.length === 0 && search.trim() && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-center justify-between" data-testid="client-not-found">
          <div className="flex items-center gap-3">
            <UserPlus className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Cliente no encontrado</p>
              <p className="text-xs text-amber-600">No se encontro ningun cliente con "{search}"</p>
            </div>
          </div>
          <Button onClick={openRegisterWithSearch} className="bg-[#1D3C6E] hover:bg-[#152B50] text-sm" data-testid="register-not-found-client">
            <Plus className="w-4 h-4 mr-1" /> Registrar cliente
          </Button>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Nombre</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Telefono</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600"></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(client)} data-testid={`client-row-${client.id}`}>
                <td className="px-4 py-3 font-medium">{client.name}</td>
                <td className="px-4 py-3 text-slate-600">{client.phone}</td>
                <td className="px-4 py-3 text-slate-600">{client.email || '-'}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(client.created_at).toLocaleDateString('es-MX')}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate('/repairs', { state: { newRepairClient: client } })}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#AFBD20]/10 text-[#1D3C6E] hover:bg-[#AFBD20]/20 text-xs font-medium transition-colors"
                    data-testid={`client-repair-${client.id}`}
                  >
                    <Wrench className="w-3 h-3" /> Reparacion
                  </button>
                </td>
              </tr>
            ))}
            {clients.length === 0 && !searched && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No hay clientes</td></tr>}
          </tbody>
        </table>
      </div>

      {/* New Client Dialog — kept as modal since the request explicitly
          concerns the *detail* view, not the create flow. */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Nuevo Cliente</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-600">Telefono *</label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></div>
              <div><label className="text-xs font-medium text-slate-600">Telefono 2</label><Input value={form.phone2} onChange={(e) => setForm({ ...form, phone2: e.target.value })} /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-600">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-xs font-medium text-slate-600">Notas</label><textarea className="w-full border border-slate-200 rounded-lg p-2 text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            {/* Datos de Facturación (opcional al crear cliente) */}
            <details className="border border-slate-200 rounded-lg">
              <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#1D3C6E]">
                Datos de Facturación (opcional)
              </summary>
              <div className="p-3 pt-1 space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Nombre / Razón Social</label>
                  <Input data-testid="client-fiscal-name-input" value={form.fiscal_name} onChange={(e) => setForm({ ...form, fiscal_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">RFC</label>
                    <Input data-testid="client-fiscal-rfc-input" value={form.fiscal_rfc} onChange={(e) => setForm({ ...form, fiscal_rfc: e.target.value.toUpperCase() })} maxLength={13} />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Código Postal</label>
                    <Input data-testid="client-fiscal-zip-input" value={form.fiscal_zip} onChange={(e) => setForm({ ...form, fiscal_zip: e.target.value.replace(/\D/g, '').slice(0, 5) })} maxLength={5} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Régimen Fiscal</label>
                  <select data-testid="client-fiscal-regime-input" className="w-full border border-slate-200 rounded-md p-2 text-sm bg-white" value={form.fiscal_regime} onChange={(e) => setForm({ ...form, fiscal_regime: e.target.value })}>
                    <option value="">Selecciona un régimen…</option>
                    {SAT_REGIMES.map(([code, label]) => (
                      <option key={code} value={code}>{code} — {label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Correo electrónico (facturación)</label>
                  <Input data-testid="client-fiscal-email-input" type="email" value={form.fiscal_email} onChange={(e) => setForm({ ...form, fiscal_email: e.target.value })} />
                </div>
              </div>
            </details>

            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="submit-client-button">Guardar Cliente</Button>
            <Button type="button" onClick={handleCreateAndRepair} className="w-full bg-[#AFBD20] hover:bg-[#9CA819] text-slate-900 font-medium" data-testid="submit-client-repair-button">
              <Wrench className="w-4 h-4 mr-2" /> Guardar y Crear Reparacion
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 flex items-center gap-1">{icon}<span>{value}</span></p>
    </div>
  );
}
